import React, { useMemo, useCallback } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import StickyScrollTable from '../../../../components/common/StickyScrollTable';
import PreferencesDataTable from '../../../../components/common/PreferencesDataTable';

const TEXT_FIELDS = [
    'yil',
    'ay',
    'bosBranchCode',
    'bayiAdi',
    'karsanGarantiliParca',
    'musteriOderParca',
    'servisGarantiliParca',
    'karsanGarantiliLabor',
    'musteriOderLabor',
    'servisGarantiliLabor',
    'karsanGarantiliOther',
    'musteriOderOther',
    'servisGarantiliOther',
    'karsanParcaTutari',
    'musteriParcaTutari',
    'servisParcaTutari',
    'karsanLaborTutari',
    'musteriLaborTutari',
    'servisLaborTutari',
    'karsanOtherTutari',
    'musteriOtherTutari',
    'servisOtherTutari'
];

const DealerProcessingReportTable = ({ items, totalCount, loading, lazyParams, onPage, activeFilters, onFiltersChange, sortField, sortOrder, onSortChange }) => {
    const { th } = useLanguage();

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const tableFilters = useMemo(
        () => ({
            yil: { value: activeFilters?.yil ?? null, matchMode: activeFilters?.yilMatchMode ?? FilterMatchMode.CONTAINS },
            ay: { value: activeFilters?.ay ?? null, matchMode: activeFilters?.ayMatchMode ?? FilterMatchMode.CONTAINS },
            bosBranchCode: { value: activeFilters?.bosBranchCode ?? null, matchMode: activeFilters?.bosBranchCodeMatchMode ?? FilterMatchMode.CONTAINS },
            bayiAdi: { value: activeFilters?.bayiAdi ?? null, matchMode: activeFilters?.bayiAdiMatchMode ?? FilterMatchMode.CONTAINS },
            karsanGarantiliParca: { value: activeFilters?.karsanGarantiliParca ?? null, matchMode: activeFilters?.karsanGarantiliParcaMatchMode ?? FilterMatchMode.CONTAINS },
            musteriOderParca: { value: activeFilters?.musteriOderParca ?? null, matchMode: activeFilters?.musteriOderParcaMatchMode ?? FilterMatchMode.CONTAINS },
            servisGarantiliParca: { value: activeFilters?.servisGarantiliParca ?? null, matchMode: activeFilters?.servisGarantiliParcaMatchMode ?? FilterMatchMode.CONTAINS },
            karsanGarantiliLabor: { value: activeFilters?.karsanGarantiliLabor ?? null, matchMode: activeFilters?.karsanGarantiliLaborMatchMode ?? FilterMatchMode.CONTAINS },
            musteriOderLabor: { value: activeFilters?.musteriOderLabor ?? null, matchMode: activeFilters?.musteriOderLaborMatchMode ?? FilterMatchMode.CONTAINS },
            servisGarantiliLabor: { value: activeFilters?.servisGarantiliLabor ?? null, matchMode: activeFilters?.servisGarantiliLaborMatchMode ?? FilterMatchMode.CONTAINS },
            karsanGarantiliOther: { value: activeFilters?.karsanGarantiliOther ?? null, matchMode: activeFilters?.karsanGarantiliOtherMatchMode ?? FilterMatchMode.CONTAINS },
            musteriOderOther: { value: activeFilters?.musteriOderOther ?? null, matchMode: activeFilters?.musteriOderOtherMatchMode ?? FilterMatchMode.CONTAINS },
            servisGarantiliOther: { value: activeFilters?.servisGarantiliOther ?? null, matchMode: activeFilters?.servisGarantiliOtherMatchMode ?? FilterMatchMode.CONTAINS },
            karsanParcaTutari: { value: activeFilters?.karsanParcaTutari ?? null, matchMode: activeFilters?.karsanParcaTutariMatchMode ?? FilterMatchMode.CONTAINS },
            musteriParcaTutari: { value: activeFilters?.musteriParcaTutari ?? null, matchMode: activeFilters?.musteriParcaTutariMatchMode ?? FilterMatchMode.CONTAINS },
            servisParcaTutari: { value: activeFilters?.servisParcaTutari ?? null, matchMode: activeFilters?.servisParcaTutariMatchMode ?? FilterMatchMode.CONTAINS },
            karsanLaborTutari: { value: activeFilters?.karsanLaborTutari ?? null, matchMode: activeFilters?.karsanLaborTutariMatchMode ?? FilterMatchMode.CONTAINS },
            musteriLaborTutari: { value: activeFilters?.musteriLaborTutari ?? null, matchMode: activeFilters?.musteriLaborTutariMatchMode ?? FilterMatchMode.CONTAINS },
            servisLaborTutari: { value: activeFilters?.servisLaborTutari ?? null, matchMode: activeFilters?.servisLaborTutariMatchMode ?? FilterMatchMode.CONTAINS },
            karsanOtherTutari: { value: activeFilters?.karsanOtherTutari ?? null, matchMode: activeFilters?.karsanOtherTutariMatchMode ?? FilterMatchMode.CONTAINS },
            musteriOtherTutari: { value: activeFilters?.musteriOtherTutari ?? null, matchMode: activeFilters?.musteriOtherTutariMatchMode ?? FilterMatchMode.CONTAINS },
            servisOtherTutari: { value: activeFilters?.servisOtherTutari ?? null, matchMode: activeFilters?.servisOtherTutariMatchMode ?? FilterMatchMode.CONTAINS }
        }),
        [activeFilters]
    );

    const handleTableFilter = useCallback(
        (e) => {
            const filters = e?.filters || {};
            const updated = { ...(activeFilters || {}) };

            TEXT_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.CONTAINS;
                }
            });

            onFiltersChange?.(updated);
        },
        [activeFilters, onFiltersChange]
    );

    const formatNumber = (value, fractionDigits = 0) => {
    if (value == null || value === '') return '-';

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return value;

    return numericValue.toLocaleString('tr-TR', {
        useGrouping: false,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    });
};
    const createTextColumn = (field, header, options = {}) => {
        const { body, sortable = true, minWidth = '170px', lockedFromPrefs = false, lockedPosition } = options;
        return {
            field,
            columnKey: field,
            sortable,
            header,
            style: { minWidth },
            body,
            lockedFromPrefs,
            lockedPosition,
            filter: true,
            filterField: field,
            filterPlaceholder: th('common.search'),
            showFilterMenu: true,
            filterMatchModeOptions: textMatchModeOptions,
            showClearButton: false
        };
    };

const decimalBody = (field) => (row) => formatNumber(row?.[field], 2);
const integerBody = (field) => (row) => formatNumber(row?.[field], 0);
    const tableItems = useMemo(
        () =>
            (items ?? []).map((item, index) => ({
                ...item,
                _rowId: `${lazyParams.first}-${index}`
            })),
        [items, lazyParams.first]
    );

    const columns = [
        createTextColumn('yil', th('dealerProcessingReport.columns.yil'), { minWidth: '120px', lockedFromPrefs: true, lockedPosition: 'start' }),
        createTextColumn('ay', th('dealerProcessingReport.columns.ay'), { minWidth: '120px', lockedFromPrefs: true, lockedPosition: 'start' }),
        createTextColumn('bosBranchCode', th('dealerProcessingReport.columns.bos_branch_code'), { minWidth: '150px', lockedFromPrefs: true, lockedPosition: 'start' }),
        createTextColumn('bayiAdi', th('dealerProcessingReport.columns.bayi_adi'), { minWidth: '220px', lockedFromPrefs: true, lockedPosition: 'start' }),
        createTextColumn('karsanGarantiliParca', th('dealerProcessingReport.columns.karsan_garantili_parca'), { body: integerBody('karsanGarantiliParca') }),
        createTextColumn('musteriOderParca', th('dealerProcessingReport.columns.musteri_oder_parca'), { body: integerBody('musteriOderParca') }),
        createTextColumn('servisGarantiliParca', th('dealerProcessingReport.columns.servis_garantili_parca'), { body: integerBody('servisGarantiliParca') }),
        createTextColumn('karsanGarantiliLabor', th('dealerProcessingReport.columns.karsan_garantili_labor'), { body: integerBody('karsanGarantiliLabor') }),
        createTextColumn('musteriOderLabor', th('dealerProcessingReport.columns.musteri_oder_labor'), { body: integerBody('musteriOderLabor') }),
        createTextColumn('servisGarantiliLabor', th('dealerProcessingReport.columns.servis_garantili_labor'), { body: integerBody('servisGarantiliLabor') }),
        createTextColumn('karsanGarantiliOther', th('dealerProcessingReport.columns.karsan_garantili_other'), { body: integerBody('karsanGarantiliOther') }),
        createTextColumn('musteriOderOther', th('dealerProcessingReport.columns.musteri_oder_other'), { body: integerBody('musteriOderOther') }),
        createTextColumn('servisGarantiliOther', th('dealerProcessingReport.columns.servis_garantili_other'), { body: integerBody('servisGarantiliOther') }),
        createTextColumn('karsanParcaTutari', th('dealerProcessingReport.columns.karsan_parca_tutari'), { body: decimalBody('karsanParcaTutari') }),
        createTextColumn('musteriParcaTutari', th('dealerProcessingReport.columns.musteri_parca_tutari'), { body: decimalBody('musteriParcaTutari') }),
        createTextColumn('servisParcaTutari', th('dealerProcessingReport.columns.servis_parca_tutari'), { body: decimalBody('servisParcaTutari') }),
        createTextColumn('karsanLaborTutari', th('dealerProcessingReport.columns.karsan_labor_tutari'), { body: decimalBody('karsanLaborTutari') }),
        createTextColumn('musteriLaborTutari', th('dealerProcessingReport.columns.musteri_labor_tutari'), { body: decimalBody('musteriLaborTutari') }),
        createTextColumn('servisLaborTutari', th('dealerProcessingReport.columns.servis_labor_tutari'), { body: decimalBody('servisLaborTutari') }),
        createTextColumn('karsanOtherTutari', th('dealerProcessingReport.columns.karsan_other_tutari'), { body: decimalBody('karsanOtherTutari') }),
        createTextColumn('musteriOtherTutari', th('dealerProcessingReport.columns.musteri_other_tutari'), { body: decimalBody('musteriOtherTutari') }),
        createTextColumn('servisOtherTutari', th('dealerProcessingReport.columns.servis_other_tutari'), { body: decimalBody('servisOtherTutari') })
    ];

    return (
        <StickyScrollTable deps={[tableItems, activeFilters]} scrollBarPosition="top">
            <PreferencesDataTable
                tableKey="dealer-processing-report"
                columns={columns}
                value={tableItems}
                totalRecords={totalCount}
                className="p-datatable-gridlines"
                showGridlines
                lazy
                paginator
                first={lazyParams.first}
                rows={lazyParams.rows || 20}
                onPage={onPage}
                dataKey="_rowId"
                loading={loading}
                filterDisplay="row"
                filters={tableFilters}
                onFilter={handleTableFilter}
                emptyMessage={th('common.noRecords')}
                rowsPerPageOptions={[10, 20, 50, 100]}
                tableStyle={{ minWidth: '224rem' }}
                onSort={(e) => onSortChange?.(e)}
                sortField={sortField}
                sortOrder={sortOrder}
                sortMode="single"
                pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
            />
        </StickyScrollTable>
    );
};

export default DealerProcessingReportTable;
